/**
 * @file Racine de composition : assemble stockage, sauvegarde, Simulation, rendu, audio, entree, UI et boucle ; gere visibilite, resize et destruction.
 */
import { CONFIG } from '@config/index';
import { Simulation } from '@core/index';
import type { AmmoStyle, SfxId } from '@config/types';
import type { WeaponBehavior } from '@shared/enums';
import { detectLocale, I18n } from '@i18n/index';
import { AudioEngine } from '@platform/audio/AudioEngine';
import { DeviceHaptics } from '@platform/haptics';
import { PointerInput } from '@platform/input/PointerInput';
import { LocalStorageAdapter } from '@platform/storage/StorageAdapter';
import { SystemClock, watchVisibility } from '@platform/time';
import { GameRenderer } from '@render/GameRenderer';
import { UiApp } from '@ui/UiApp';
import { notify } from '@ui/core/feedback';
import { installFastTap } from '@ui/core/fastTap';
import type { UiContext } from '@ui/UiContext';
import { bindAudio } from './AudioBridge';
import { bindHaptics } from './HapticsBridge';
import { installDevTools } from './DevTools';
import { GameLoop } from './GameLoop';
import { CloudBridge } from './CloudBridge';
import { HubSession } from './HubSession';
import { OnlineApi } from './OnlineApi';
import { InputBridge } from './InputBridge';
import { OnlineService, resolveServerConfig } from './OnlineService';
import { SaveManager } from './SaveManager';

export interface AppElements {
  canvas: HTMLCanvasElement;
  uiRoot: HTMLElement;
  /** Surface qui recoit le doigt (joystick) : le conteneur du jeu entier. */
  inputSurface: HTMLElement;
}

export class App {
  private readonly disposers: Array<() => void> = [];

  private constructor(
    readonly sim: Simulation,
    readonly renderer: GameRenderer,
    readonly ui: UiApp,
    readonly loop: GameLoop,
    readonly saves: SaveManager,
    readonly online: OnlineService,
    readonly cloud: CloudBridge,
    readonly hub: HubSession,
  ) {}

  static async boot(el: AppElements): Promise<App> {
    const clock = new SystemClock();
    const saves = new SaveManager(new LocalStorageAdapter(), clock, CONFIG);
    const online = new OnlineService(resolveServerConfig(), () => saves.deviceId());
    const cloud = new CloudBridge(online, saves, clock);
    // sauvegarde locale, puis arbitrage avec le cloud (repli hors ligne si le serveur ne repond pas)
    const synced = await cloud.syncOnBoot(await saves.loadOrCreate(detectLocale()), detectLocale());
    const loaded = synced.loaded;
    const data = loaded.data;
    const i18n = new I18n(detectLocale(data.settings.locale));
    document.documentElement.lang = i18n.locale;

    const sim = new Simulation({ data, clock, config: CONFIG });
    const index = sim.ctx.index;
    const weaponDefOf = (uid: string): string | null => sim.data.weapons.items.find((w) => w.uid === uid)?.defId ?? null;

    const ammoStyleOf = (b: WeaponBehavior): AmmoStyle => sim.ctx.services.ammoSkins.styleOf(b);
    const renderer = new GameRenderer({ canvas: el.canvas, config: CONFIG, index, bus: sim.bus, weaponDefOf, ammoStyleOf, eco: data.settings.eco });
    const audio = new AudioEngine();
    audio.setVolume(data.settings.sfxVolume, data.settings.muted);
    audio.setMusicVolume(data.settings.musicVolume);
    const pointer = new PointerInput(el.inputSurface);

    const haptics = new DeviceHaptics();
    haptics.enabled = data.settings.vibration;
    const hub = new HubSession(sim, online, renderer);
    const onlineApi = online.enabled ? new OnlineApi(online, sim, i18n) : undefined;
    if (onlineApi) onlineApi.onRenamed = () => hub.rejoin();
    const uiCtx: UiContext = {
      sim,
      config: CONFIG,
      index,
      i18n,
      sfx: (id: SfxId) => audio.play(id),
      haptic: (kind) => haptics.play(kind),
      projector: renderer,
      hub: online.enabled ? hub : undefined,
      online: onlineApi,
      joystick: pointer.state,
      actions: {
        saveNow: () => void saves.save(sim),
        resetSave: async () => {
          app.loop.stop();
          await saves.wipe();
          await cloud.markOverwrite();
          window.location.reload();
        },
        exportSave: () => saves.exportString(sim),
        importSave: async (json) => {
          const ok = await saves.importString(json, i18n.locale);
          if (ok) {
            await cloud.markOverwrite();
            window.location.reload();
          }
          return ok;
        },
        gpuInfo: () => ({ ...renderer.memory() }),
      },
    };
    const ui = new UiApp(uiCtx, el.uiRoot);
    // Les reglages (volume, mute, eco) sont ecrits par la commande settings.set : on les repercute a chaud quand ils changent.
    renderer.setQuality(data.settings.quality);
    renderer.setFxVisibility(data.settings.fxSelf, data.settings.fxOthers);
    renderer.onAutoLowQuality = () => notify(i18n.t('settings.autoLowQuality'));
    let applied = { vol: data.settings.sfxVolume, music: data.settings.musicVolume, muted: data.settings.muted, eco: data.settings.eco, quality: data.settings.quality };
    const applySettings = (): void => {
      const st = sim.data.settings;
      if (st.sfxVolume !== applied.vol || st.muted !== applied.muted) audio.setVolume(st.sfxVolume, st.muted);
      if (st.musicVolume !== applied.music) audio.setMusicVolume(st.musicVolume);
      if (st.eco !== applied.eco) renderer.setEco(st.eco);
      if (st.quality !== applied.quality) renderer.setQuality(st.quality);
      haptics.enabled = st.vibration;
      renderer.setFxVisibility(st.fxSelf, st.fxOthers);
      applied = { vol: st.sfxVolume, music: st.musicVolume, muted: st.muted, eco: st.eco, quality: st.quality };
    };
    const bridge = new InputBridge(sim, pointer.state);
    const loop = new GameLoop(CONFIG.sim.dt, CONFIG.sim.maxCatchUpTicks, {
      tick: () => {
        bridge.apply();
        sim.step();
      },
      frame: (alpha, dt, time) => {
        applySettings();
        hub.update(dt);
        renderer.render(sim.getView(), alpha, dt, time);
        ui.update(dt);
      },
    });
    const app = new App(sim, renderer, ui, loop, saves, online, cloud, hub);

    // --- cablage
    app.disposers.push(pointer.attach());
    app.disposers.push(installFastTap(document));
    app.disposers.push(bindAudio(sim.bus, audio));
    app.disposers.push(bindHaptics(sim.bus, haptics));
    const unlockAudio = (): void => audio.unlock();
    window.addEventListener('pointerdown', unlockAudio, { once: true });
    window.addEventListener('keydown', unlockAudio, { once: true });

    const resize = (): void => renderer.resize(el.canvas.clientWidth, el.canvas.clientHeight, window.devicePixelRatio || 1);
    const ro = new ResizeObserver(resize);
    ro.observe(el.canvas);
    resize();
    app.disposers.push(() => ro.disconnect());

    app.disposers.push(
      watchVisibility({
        onHidden: () => {
          sim.markSeen(clock.nowMs());
          void saves.save(sim).then(() => cloud.flush(true));
          loop.stop();
          audio.setPaused(true);
        },
        onVisible: () => {
          sim.resume(clock.nowMs());
          loop.start();
          audio.setPaused(false);
        },
      }),
    );
    app.disposers.push(saves.startAutosave(sim));
    app.disposers.push(cloud.start());
    app.disposers.push(() => hub.dispose());
    if (onlineApi) app.disposers.push(onlineApi.start());
    app.disposers.push(() => audio.dispose());

    // --- demarrage
    sim.start();
    ui.mount();
    if (loaded.recovered) sim.bus.emit('Toast', { key: 'toast.loadFailed', kind: 'warning' });
    if (synced.toast) sim.bus.emit('Toast', { key: synced.toast, kind: 'info' });
    loop.start();
    if (import.meta.env.DEV) installDevTools(app);
    return app;
  }

  dispose(): void {
    this.loop.stop();
    for (const d of this.disposers.splice(0)) d();
    this.ui.destroy();
    this.renderer.dispose();
  }
}
